import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

interface EditWarehousesProps {
  warehouse?: any;
  onBack?: () => void;
}

const EditWarehouses: React.FC<EditWarehousesProps> = ({ warehouse: propWarehouse, onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const warehouse = propWarehouse ?? location.state?.warehouse;
  const handleBack = () => {
    if (onBack) { onBack(); } else { navigate(-1); }
  };
  // Collapsible section states
  const [sections, setSections] = useState({
    details: true,
    tax: true,
    terms: true,
  });

  const toggleSection = (section: 'details' | 'tax' | 'terms') => {
    setSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Form states initialized with mockup mock-values
  const [formData, setFormData] = useState({
    // Left Column
    name: warehouse?.name || 'Vashi-Depo',
    address: '',
    pincode: '',
    district: '',
    city: '',
    state: '',
    phoneCode: '+91',
    phoneNo: '',
    email: '',
    erpId: warehouse?.erpId || '',
    popularity: '',
    businessType: '',
    warehouseCategory: '',
    warehouseLabel: '',
    defaultCreditLimit: '',
    defaultCreditPeriod: '',
    defaultMinOrderValue: '',
    cinNo: '',
    areaPincode: '',

    // Right Column
    parentWarehouse: '',
    discountBudget: '',
    arakatDiscount: '',
    suffixToBillNo: 'Select Role',
    prefixToCreditNo: '',
    suffixToCreditNo: '',
    prefixToDebitNo: '',
    suffixToDebitNo: '',
    prefixToSaleReturnNo: '',
    suffixToSaleReturnNo: '',
    prefixChallanNo: '',
    suffixChallanNo: '',
    prefixToCollectionNo: '',
    suffixToCollectionNo: '',
    prefixToVansaleBill: '',
    suffixToVansaleBill: '',
    newOutletDiscountCatId: '',
    zone: warehouse?.zone || 'Mumbai',
    subzone: warehouse?.subzone || 'Vashi',
    invoiceHeight: 'Mumbai',
    invoiceWidth: 'Vashi',
    currency: 'INR',
    vDiscountPercent: 'Vashi',
    qrCodeImage: null,

    // Checkboxes
    isActive: warehouse ? warehouse.status === 'Active' : true,
    printReceiptDotMatrix: false,
    printReceiptRegular: true,
    enableShipToPrimary: false,
    nonTransactional: warehouse ? warehouse.nonTransactional === 'Yes' : false,

    // Tax Details
    warehouseTin: '',
    warehousePan: '',
    gstRegisteredAddress: '',
    fssaiNo: '',
    warehouseGst: '',
    gstin: warehouse?.name || 'Vashi-Depo',
    warehouseLbt: '',
    jurisdiction: 'Mumbai',

    // Terms
    declaration: '',
    applyStateDistrictCityAll: false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert('Warehouse details updated successfully!');
    handleBack();
  };

  return (
    <>
      {/* Sub-Header Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        
        {/* Title and Back */}
        <div className="flex items-center gap-3">
          <button onClick={handleBack}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
            title="Go Back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight m-0">
            Edit Warehouses
          </h1>
        </div>

        {/* Right side Red Button */}
        <div>
          <button 
            type="button"
            className="px-4 py-2 go_erp-bg text-white text-sm font-medium rounded-xl cursor-pointer transition-all"
          >
            Update Bills/Retail/Vansale Nos
          </button>
        </div>

      </div>

      {/* S.No reset buttons panel */}
      <div className="flex flex-wrap gap-2.5 justify-center mb-6">
        {[
          'Reset Bill S.No.',
          'Reset Credit S.No.',
          'Reset Debit S.No.',
          'Reset Sale Return S.No.',
          'Reset Delivery Challan S.No.',
          'Reset Collection S.No.',
          'Reset Van Sale Bill S.No.',
          'Reset Van Sale DB S.No.'
        ].map((btnLabel) => (
          <button
            key={btnLabel}
            type="button"
            className="bg-[#4D3B31] text-white text-xs font-regular px-4 py-2 rounded-lg cursor-pointer transition-colors"
          >
            {btnLabel}
          </button>
        ))}
      </div>

      {/* Form Container */}
      <form onSubmit={handleSubmit}>
        
        {/* Section 1: Warehouse Details */}
        <div className="bg-white rounded-2xl shadow-sm border border-[#ECECF2] overflow-hidden">
          {/* Header Banner */}
          <button
            type="button"
            onClick={() => toggleSection('details')}
            className="w-full px-4 py-3 flex items-center justify-between bg-white border-b border-gray-200 cursor-pointer transition-colors"
          >
            <span className="text-sm font-semibold text-black">Warehouse Details</span>
            <svg 
              className={`w-4 h-4 text-slate-500 transition-transform duration-250 ${sections.details ? 'rotate-180' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Section Body */}
          {sections.details && (
            <div className="p-4  animate-fadeIn">
              
              {/* TWO COLUMN GRID FOR DETAILS */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                
                {/* LEFT COLUMN */}
                <div className="space-y-2">
                  {/* Name */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-black">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter Tags"
                      className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                    />
                  </div>

                  {/* Address */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-black">Address</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Enter Tags"
                      className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors resize-none hide-scrollbar h-[104px]"
                    />
                  </div>

                  {/* Pincode & District */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Pincode</label>
                      <input
                        type="text"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleChange}
                        placeholder="Enter Item Code"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">District</label>
                      <select
                        name="district"
                        value={formData.district}
                        onChange={handleChange}
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors bg-white cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
                      >
                        <option value="">Enter Sale Unit</option>
                        <option value="Thane">Thane</option>
                        <option value="Mumbai">Mumbai</option>
                        <option value="Pune">Pune</option>
                      </select>
                    </div>
                  </div>

                  {/* City & State */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">City</label>
                      <select
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors bg-white cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
                      >
                        <option value="">Enter Tags</option>
                        <option value="Navi Mumbai">Navi Mumbai</option>
                        <option value="Mumbai">Mumbai</option>
                        <option value="Pune">Pune</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">State</label>
                      <select
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors bg-white cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
                      >
                        <option value="">Enter Tags</option>
                        <option value="Maharashtra">Maharashtra</option>
                        <option value="Gujarat">Gujarat</option>
                        <option value="Delhi">Delhi</option>
                      </select>
                    </div>
                  </div>

                  {/* Phone No */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-black">Phone No.</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="phoneCode"
                        value={formData.phoneCode}
                        onChange={handleChange}
                        className="w-20 border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors text-center"
                      />
                      <input
                        type="text"
                        name="phoneNo"
                        value={formData.phoneNo}
                        onChange={handleChange}
                        placeholder="Enter Tags"
                        className="flex-1 border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-black">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter Tags"
                      className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                    />
                  </div>

                  {/* ERP ID */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-black">ERP/SAP ID</label>
                    <input
                      type="text"
                      name="erpId"
                      value={formData.erpId}
                      onChange={handleChange}
                      placeholder="Enter Tags"
                      className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                    />
                  </div>

                  {/* Popularity & Business type */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Popstrata</label>
                      <select
                        name="popularity"
                        value={formData.popularity}
                        onChange={handleChange}
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors bg-white cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
                      >
                        <option value="">Enter Tags</option>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Business type</label>
                      <select
                        name="businessType"
                        value={formData.businessType}
                        onChange={handleChange}
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors bg-white cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
                      >
                        <option value="">Enter Tags</option>
                        <option value="Distributor">Distributor</option>
                        <option value="Retailer">Retailer</option>
                        <option value="Wholesaler">Wholesaler</option>
                      </select>
                    </div>
                  </div>

                  {/* Warehouse Category */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-black">Warehouse Category</label>
                    <select
                      name="warehouseCategory"
                      value={formData.warehouseCategory}
                      onChange={handleChange}
                      className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors bg-white cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
                    >
                      <option value="">Enter Tags</option>
                      <option value="Depot">Depot</option>
                      <option value="Store">Store</option>
                    </select>
                  </div>

                  {/* WAREHOUSE Label */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-black">WAREHOUSE Label</label>
                    <select
                      name="warehouseLabel"
                      value={formData.warehouseLabel}
                      onChange={handleChange}
                      className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors bg-white cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
                    >
                      <option value="">Enter Tags</option>
                      <option value="Vashi Main">Vashi Main</option>
                      <option value="Malad Branch">Malad Branch</option>
                    </select>
                  </div>

                  {/* Default Credit Limit & Default Credit Period */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Default Credit Limit</label>
                      <input
                        type="text"
                        name="defaultCreditLimit"
                        value={formData.defaultCreditLimit}
                        onChange={handleChange}
                        placeholder="Enter Item Code"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Default Credit Period</label>
                      <select
                        name="defaultCreditPeriod"
                        value={formData.defaultCreditPeriod}
                        onChange={handleChange}
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors bg-white cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
                      >
                        <option value="">Enter Sale Unit</option>
                        <option value="15 Days">15 Days</option>
                        <option value="30 Days">30 Days</option>
                      </select>
                    </div>
                  </div>

                  {/* Default Minimum Order Value & CIN No */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Default Minimum Order Value</label>
                      <select
                        name="defaultMinOrderValue"
                        value={formData.defaultMinOrderValue}
                        onChange={handleChange}
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors bg-white cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
                      >
                        <option value="">Enter Tags</option>
                        <option value="5000">5000</option>
                        <option value="10000">10000</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">CIN No</label>
                      <select
                        name="cinNo"
                        value={formData.cinNo}
                        onChange={handleChange}
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors bg-white cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
                      >
                        <option value="">Enter Tags</option>
                        <option value="CIN123">CIN123</option>
                      </select>
                    </div>
                  </div>

                  {/* Area pin-code */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-black">Area pin code</label>
                    <select
                      name="areaPincode"
                      value={formData.areaPincode}
                      onChange={handleChange}
                      className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors bg-white cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
                    >
                      <option value="">Enter Tags</option>
                      <option value="400703">400703</option>
                    </select>
                  </div>

                </div>

                {/* RIGHT COLUMN */}
                <div className="space-y-2">
                  {/* Parent Warehouse */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-black">Parent WAREHOUSE.</label>
                    <select
                      name="parentWarehouse"
                      value={formData.parentWarehouse}
                      onChange={handleChange}
                      className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors bg-white cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
                    >
                      <option value="">Enter Tags</option>
                      <option value="main">Main Depot</option>
                    </select>
                  </div>

                  {/* Discount Budget & Arakat Discount */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Discount budget</label>
                      <input
                        type="text"
                        name="discountBudget"
                        value={formData.discountBudget}
                        onChange={handleChange}
                        placeholder="Enter Tags"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Availed discount</label>
                      <input
                        type="text"
                        name="arakatDiscount"
                        value={formData.arakatDiscount}
                        onChange={handleChange}
                        placeholder="Enter Tags"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Suffix To Bill No */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-black">Suffix To Bill No</label>
                    <input
                      type="text"
                      name="suffixToBillNo"
                      value={formData.suffixToBillNo}
                      onChange={handleChange}
                      placeholder="Select Role"
                      className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                    />
                  </div>

                  {/* Prefix To Credit No & Suffix To Credit No */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Prefix To Credit No.</label>
                      <input
                        type="text"
                        name="prefixToCreditNo"
                        value={formData.prefixToCreditNo}
                        onChange={handleChange}
                        placeholder="Enter Tags"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Suffix To Credit No.</label>
                      <input
                        type="text"
                        name="suffixToCreditNo"
                        value={formData.suffixToCreditNo}
                        onChange={handleChange}
                        placeholder="Select Designation"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Prefix To Debit No & Suffix To Debit No */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Prefix To Debit No.</label>
                      <input
                        type="text"
                        name="prefixToDebitNo"
                        value={formData.prefixToDebitNo}
                        onChange={handleChange}
                        placeholder="Enter Tags"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Suffix To Debit No.</label>
                      <input
                        type="text"
                        name="suffixToDebitNo"
                        value={formData.suffixToDebitNo}
                        onChange={handleChange}
                        placeholder="Select Designation"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Prefix To Sale return No & Suffix To Sale return no */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Prefix To Sale return No</label>
                      <input
                        type="text"
                        name="prefixToSaleReturnNo"
                        value={formData.prefixToSaleReturnNo}
                        onChange={handleChange}
                        placeholder="Enter Tags"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Suffix To Sale return no</label>
                      <input
                        type="text"
                        name="suffixToSaleReturnNo"
                        value={formData.suffixToSaleReturnNo}
                        onChange={handleChange}
                        placeholder="Select Designation"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Prefix Challan No & Suffix Challan No */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Prefix Challan No</label>
                      <input
                        type="text"
                        name="prefixChallanNo"
                        value={formData.prefixChallanNo}
                        onChange={handleChange}
                        placeholder="Enter Tags"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Suffix Challan No</label>
                      <input
                        type="text"
                        name="suffixChallanNo"
                        value={formData.suffixChallanNo}
                        onChange={handleChange}
                        placeholder="Select Designation"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Prefix To Collection no & Suffix To Collection no */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Prefix To Collection no</label>
                      <input
                        type="text"
                        name="prefixToCollectionNo"
                        value={formData.prefixToCollectionNo}
                        onChange={handleChange}
                        placeholder="Enter Tags"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Suffix To Collection no</label>
                      <input
                        type="text"
                        name="suffixToCollectionNo"
                        value={formData.suffixToCollectionNo}
                        onChange={handleChange}
                        placeholder="Select Designation"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Prefix To Vansale Bill & Suffix To Vansale Bill */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Prefix To Vansale Bill</label>
                      <input
                        type="text"
                        name="prefixToVansaleBill"
                        value={formData.prefixToVansaleBill}
                        onChange={handleChange}
                        placeholder="Enter Tags"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Suffix To Vansale Bill</label>
                      <input
                        type="text"
                        name="suffixToVansaleBill"
                        value={formData.suffixToVansaleBill}
                        onChange={handleChange}
                        placeholder="Select Designation"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* New Outlet Discount Category Id */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-black">New Outlet Discount Category Id</label>
                    <select
                      name="newOutletDiscountCatId"
                      value={formData.newOutletDiscountCatId}
                      onChange={handleChange}
                      className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors bg-white cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
                    >
                      <option value="">Enter Tags</option>
                      <option value="CAT-01">CAT-01</option>
                    </select>
                  </div>

                  {/* Zone & Subzone */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Zone</label>
                      <select
                        name="zone"
                        value={formData.zone}
                        onChange={handleChange}
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors bg-white cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
                      >
                        <option value="Mumbai">Mumbai</option>
                        <option value="Pune">Pune</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Subzone</label>
                      <select
                        name="subzone"
                        value={formData.subzone}
                        onChange={handleChange}
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors bg-white cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
                      >
                        <option value="Vashi">Vashi</option>
                        <option value="Malad">Malad</option>
                      </select>
                    </div>
                  </div>

                  {/* Invoice height & Invoice width */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Invoice height</label>
                      <input
                        type="text"
                        name="invoiceHeight"
                        value={formData.invoiceHeight}
                        onChange={handleChange}
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Invoice width</label>
                      <input
                        type="text"
                        name="invoiceWidth"
                        value={formData.invoiceWidth}
                        onChange={handleChange}
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Currency & V discount percent */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Currency</label>
                      <select
                        name="currency"
                        value={formData.currency}
                        onChange={handleChange}
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors bg-white cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
                      >
                        <option value="INR">INR</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">V discount percent</label>
                      <input
                        type="text"
                        name="vDiscountPercent"
                        value={formData.vDiscountPercent}
                        onChange={handleChange}
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* QR Code Image */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-black">QR Code Image</label>
                    <div className="flex items-center justify-center border border-[#828282] rounded-xl bg-white h-[38px]">
                      <label className="w-6 h-6 rounded-full bg-[#828282] hover:bg-gray-500 transition-colors flex items-center justify-center cursor-pointer select-none text-white font-bold">
                        <input type="file" className="hidden" />
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                      </label>
                    </div>
                  </div>

                </div>

              </div>

              {/* Checkboxes Area */}
              <div className="flex flex-wrap items-center gap-x-8 gap-y-4 pt-6 border-t border-slate-100 justify-center">
                
                {/* Is Active */}
                <label className="flex items-center gap-2 text-xs font-bold go_erp-text cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 rounded-sm border-[#828282] go_erp-text focus:ring-[#C62828]"
                  />
                  <span>Is Active</span>
                </label>

                {/* Print Receipt with Dot Matrix Printer */}
                <label className="flex items-center gap-2 text-xs font-bold go_erp-text cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="printReceiptDotMatrix"
                    checked={formData.printReceiptDotMatrix}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 rounded-sm border-[#828282] go_erp-text focus:ring-[#C62828]"
                  />
                  <span>Print Receipt with Dot Matrix Printer</span>
                </label>

                {/* Print Receipt with Regular Printer */}
                <label className="flex items-center gap-2 text-xs font-bold go_erp-text cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="printReceiptRegular"
                    checked={formData.printReceiptRegular}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 rounded-sm border-[#828282] go_erp-text focus:ring-[#C62828]"
                  />
                  <span>Print Receipt with Regular Printer</span>
                </label>

                {/* Enable Ship To Primary Orders */}
                <label className="flex items-center gap-2 text-xs font-bold go_erp-text cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="enableShipToPrimary"
                    checked={formData.enableShipToPrimary}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 rounded-sm border-[#828282] go_erp-text focus:ring-[#C62828]"
                  />
                  <span>Enable Ship To Primary Orders</span>
                </label>

                {/* Non Transactional */}
                <label className="flex items-center gap-2 text-xs font-bold go_erp-text cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="nonTransactional"
                    checked={formData.nonTransactional}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 rounded-sm border-[#828282] go_erp-text focus:ring-[#C62828]"
                  />
                  <span>Non Transactional</span>
                </label>

              </div>

            </div>
          )}
        </div>

        {/* Section 2: Tax Details */}
        <div className="bg-white rounded-2xl shadow-sm border border-[#ECECF2] overflow-hidden mt-4 mb-4">
          {/* Header Banner */}
          <button
            type="button"
            onClick={() => toggleSection('tax')}
            className="w-full px-4 py-3 flex items-center justify-between bg-white border-b border-[#ECECF2] cursor-pointer hover:bg-slate-50/30 transition-colors text-left"
          >
            <span className="text-xs font-bold text-slate-800 tracking-wide">Tax Details</span>
            <svg 
              className={`w-4 h-4 text-slate-500 transition-transform duration-255 ${sections.tax ? 'rotate-180' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Section Body */}
          {sections.tax && (
            <div className="p-4 animate-fadeIn">
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                
                {/* Left side inputs */}
                <div className="space-y-2">
                  
                  {/* Warehouse TIN & Warehouse PAN no */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Warehouse TIN</label>
                      <input
                        type="text"
                        name="warehouseTin"
                        value={formData.warehouseTin}
                        onChange={handleChange}
                        placeholder="Enter Item Code"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Warehouse PAN no</label>
                      <input
                        type="text"
                        name="warehousePan"
                        value={formData.warehousePan}
                        onChange={handleChange}
                        placeholder="Enter Sale Unit"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* FSSAI No & Warehouse CST */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">FSSAI No</label>
                      <input
                        type="text"
                        name="fssaiNo"
                        value={formData.fssaiNo}
                        onChange={handleChange}
                        placeholder="Enter Tags"
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-black">Warehouse CST</label>
                      <select
                        name="warehouseGst"
                        value={formData.warehouseGst}
                        onChange={handleChange}
                        className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors bg-white cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
                      >
                        <option value="">Enter Tags</option>
                        <option value="GST-MAIN">GST Main Depot</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Right side tall textarea */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-black">GST Registered Address</label>
                  <textarea
                    name="gstRegisteredAddress"
                    value={formData.gstRegisteredAddress}
                    onChange={handleChange}
                    placeholder="Enter Tags"
                    className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors resize-none h-[104px]"
                  />
                </div>
              
                {/* GSTIN */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-black">GSTIN</label>
                  <input
                    type="text"
                    name="gstin"
                    value={formData.gstin}
                    onChange={handleChange}
                    placeholder="Enter Tags"
                    className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                  />
                </div>

                {/* Bottom LBT and Jurisdiction Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-black">Warehouse LBT no</label>
                    <input
                      type="text"
                      name="warehouseLbt"
                      value={formData.warehouseLbt}
                      onChange={handleChange}
                      placeholder="Enter Tags"
                      className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-black">Jurisdiction</label>
                    <input
                      type="text"
                      name="jurisdiction"
                      value={formData.jurisdiction}
                      onChange={handleChange}
                      placeholder="Enter Tags"
                      className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Section 3: Terms and Conditions */}
        <div className="bg-white rounded-2xl shadow-sm border border-[#ECECF2] overflow-hidden">
          {/* Header Banner */}
          <button
            type="button"
            onClick={() => toggleSection('terms')}
            className="w-full px-4 py-3 flex items-center justify-between bg-white border-b border-[#ECECF2] cursor-pointer hover:bg-slate-50/30 transition-colors text-left"
          >
            <span className="text-xs font-bold text-slate-800 tracking-wide">Terms and Conditions</span>
            <svg 
              className={`w-4 h-4 text-slate-500 transition-transform duration-255 ${sections.terms ? 'rotate-180' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Section Body */}
          {sections.terms && (
            <div className="p-4 space-y-1 animate-fadeIn">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-black">Declaration</label>
                <textarea
                  name="declaration"
                  rows={2}
                  value={formData.declaration}
                  onChange={handleChange}
                  placeholder="Note"
                  className="w-full border border-[#828282] rounded-xl px-4 py-2.5 text-xs text-[#1F1F1F] outline-none transition-colors resize-none"
                />
              </div>
            </div>
          )}
        </div>

        {/* Bottom Options & Submit */}
        <div className="flex flex-col items-center gap-4 mt-4 ">
          
          {/* Apply checkbox */}
          <label className="flex items-center gap-2 text-xs font-semibold go_erp-text cursor-pointer select-none">
            <input
              type="checkbox"
              name="applyStateDistrictCityAll"
              checked={formData.applyStateDistrictCityAll}
              onChange={handleCheckboxChange}
              className="w-4 h-4 go_erp-text border-[#828282] rounded-xl"
            />
            <span>Apply State, District and City for all Outlets in all Areas.</span>
          </label>

          {/* Golden Yellow Submit Button */}
          <button
            type="submit"
            className="px-4 py-2 bg-[#D49804] text-white text-sm font-medium rounded-xl cursor-pointer transition-all"
          >
            Submit
          </button>

        </div>

      </form>
    </>
  );
};

export default EditWarehouses;